// 主题色配置与切换：默认微信绿，可选 B站蓝 / B站粉 / Qoder绿 / 小红书红 / 阿里云橙 / 涂鸦紫 / Flyme蓝 / 豆沙绿
// 色板定义见 @/style/theme.less，通过在 html 上切换 theme-xxx 类实现
export const themeList = [
  { name: "biliBlue", title: "B站蓝", color: "#00AEEC" },
  { name: "biliPink", title: "B站粉", color: "#FB7299" },
  { name: "wechatGreen", title: "微信绿", color: "#07C160" },
  { name: "qoderGreen", title: "Qoder绿", color: "#00B96B" },
  { name: "beanGreen", title: "豆沙绿", color: "#52A05A" },
  { name: "xhsRed", title: "小红书红", color: "#FE2C55" },
  { name: "flymeBlue", title: "Flyme蓝", color: "#33A3FF" },
  { name: "aliyunOrange", title: "阿里云橙", color: "#FF6A00" },
  { name: "tuyaPurple", title: "涂鸦紫", color: "#7B5CFF" }
];

const THEME_KEY = "appTheme";

// 获取当前主题名（默认微信绿）
export function getTheme() {
  const name = localStorage.getItem(THEME_KEY);
  return themeList.some(t => t.name === name) ? name : "wechatGreen";
}

// 缓存原始 favicon 的 base64，避免重复 fetch
let _faviconBase64 = null;

// 将原始 favicon.ico 读取为 base64（仅首次 fetch，后续用缓存）
function fetchOriginalFavicon() {
  if (_faviconBase64) return Promise.resolve(_faviconBase64);
  return fetch(process.env.BASE_URL + "favicon.ico")
    .then((r) => r.blob())
    .then((blob) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          _faviconBase64 = reader.result;
          resolve(_faviconBase64);
        };
        reader.readAsDataURL(blob);
      });
    })
    .catch(() => {
      // fetch 失败时回退：不嵌入原图，仅用主题色块
      return null;
    });
}

// 动态生成跟随主题色的 SVG favicon（主题色圆角背景 + 原始 LOGO 居中），替换 index.html 里的静态 favicon.ico
export function applyFavicon(name) {
  const theme = themeList.find(t => t.name === (name || getTheme()));
  const color = theme ? theme.color : "#07C160";

  fetchOriginalFavicon().then((base64) => {
    let svg;
    if (base64) {
      // 主题色圆角背景 + 原始 LOGO 居中叠加
      svg =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
        `<rect width="64" height="64" rx="14" fill="${color}"/>` +
        `<image href="${base64}" x="6" y="6" width="52" height="52" preserveAspectRatio="xMidYMid meet"/>` +
        `</svg>`;
    } else {
      // 回退：仅主题色圆角块
      svg =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
        `<rect width="64" height="64" rx="14" fill="${color}"/>` +
        `</svg>`;
    }
    const href = "data:image/svg+xml," + encodeURIComponent(svg);
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.type = "image/svg+xml";
    link.href = href;
  });
}

// 应用主题：记录选择并在 html 上切换 theme-xxx 类
export function applyTheme(name) {
  if (!themeList.some(t => t.name === name)) {
    name = "wechatGreen";
  }
  localStorage.setItem(THEME_KEY, name);
  const classList = document.documentElement.classList;
  themeList.forEach(t => classList.remove(`theme-${t.name}`));
  classList.add(`theme-${name}`);
  // 浏览器标签页图标同步跟随主题色
  applyFavicon(name);
}

// ---------- 护眼深色模式：独立于颜色主题的明暗切换 ----------
const DARK_KEY = "appDarkMode";

// 获取深色模式状态（localStorage 无记录时跟随浏览器/系统深色模式偏好）
export function getDarkMode() {
  const val = localStorage.getItem(DARK_KEY);
  if (val !== null) return val === "1";
  // 首次访问：跟随系统深色模式
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// 切换深色模式：在 html 上增删 theme-dark 类，不影响当前颜色主题
export function toggleDarkMode() {
  const isDark = !getDarkMode();
  localStorage.setItem(DARK_KEY, isDark ? "1" : "0");
  document.documentElement.classList.toggle("theme-dark", isDark);
  return isDark;
}

// 初始化深色模式状态（启动时调用，恢复上次选择）
export function applyDarkMode() {
  document.documentElement.classList.toggle("theme-dark", getDarkMode());
}

// ---------- 致哀模式：纪念日自动全站置灰（样式见 theme.less 的 .mourning） ----------
// 每年固定的纪念日（MM-DD）：09-03 抗战胜利、09-18 九一八、05-12 汶川纪念、12-13 国家公祭
const MOURNING_MONTH_DAY = ["09-03", "09-18", "05-12", "12-13"];
// 一次性全国哀悼日（YYYY-MM-DD），以国务院公告为准，需要时在此追加
const MOURNING_FULL_DATE = [];

function padZero(n) {
  return n < 10 ? "0" + n : "" + n;
}

// 判断指定日期（默认今天）是否为纪念日
export function isMourningDay(date) {
  const d = date || new Date();
  const full = `${d.getFullYear()}-${padZero(d.getMonth() + 1)}-${padZero(d.getDate())}`;
  if (MOURNING_FULL_DATE.indexOf(full) > -1) {
    return true;
  }
  return MOURNING_MONTH_DAY.indexOf(full.slice(5)) > -1;
}

// 应用致哀模式：纪念日当天在 html 上加 mourning 类，次日自动恢复
export function applyMourning() {
  document.documentElement.classList.toggle("mourning", isMourningDay());
}
